// Doc migrator — upgrades older CallSheetDoc shapes to the current shape.
//
// Runs on every read in the worker (cheap, idempotent) so editors and the
// public view never see a V1 doc. A doc is only persisted in the new shape
// the next time the user saves; the admin backfill endpoint forces this for
// every live doc in one pass.

import {
  CALLSHEET_SCHEMA_VERSION,
  type CallSheetDoc,
  type LocationRow,
  type MandatoryShotRow,
  type ReferenceLocationRow,
  type ScheduleRow,
  type ScheduleHighlight,
  type ShotCategory,
} from './types';
import { CLIENT_SCHEDULE_SECTIONS, SECTIONS, type Section } from './sections';
import { normalizeStoredUrl } from './safeUrl';
import { parsePublicScriptMeta, parseScriptMeta } from './scriptMeta';
import { assignShotIds } from './shotIdentity';

const CLIENT_SCHEDULE_SECTION_SET = new Set<Section>(CLIENT_SCHEDULE_SECTIONS);
const ENABLED_SECTION_SET = new Set<Section>(
  SECTIONS.map((section) => section.id).filter((id) => !CLIENT_SCHEDULE_SECTION_SET.has(id)),
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function stringValue(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : undefined;
}

const OBJECT_FIELDS = ['client', 'project', 'producer', 'safety', 'filmingSpecs'] as const;
const ARRAY_FIELDS = [
  'referenceLocations',
  'crew',
  'talent',
  'attendees',
  'interviewQuestions',
  'schedule',
  'mandatoryShots',
] as const;

export type DocumentValidationResult =
  | { ok: true; doc: CallSheetDoc }
  | { ok: false; errors: string[] };

/** Validate the outer document contract at an untrusted save boundary, then
 * coerce individual nested rows through the migrator. Legacy reads continue
 * to use migrateDoc directly so absent old fields can still be defaulted. */
export function validateAndMigrateDoc(raw: unknown, sheetId = ''): DocumentValidationResult {
  if (!isRecord(raw)) return { ok: false, errors: ['doc must be an object'] };
  const errors: string[] = [];
  for (const field of OBJECT_FIELDS) {
    if (field in raw && !isRecord(raw[field])) errors.push(`${field} must be an object`);
  }
  for (const field of ARRAY_FIELDS) {
    if (field in raw && !Array.isArray(raw[field])) errors.push(`${field} must be an array`);
  }
  if (isRecord(raw.project) && 'shootDates' in raw.project && !Array.isArray(raw.project.shootDates)) {
    errors.push('project.shootDates must be an array');
  }
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, doc: migrateDoc(raw, sheetId) };
}

/**
 * Best-effort deep migration. Accepts anything that parsed out of doc_json
 * (typed as `unknown` because old docs may be missing fields the type
 * declares as required). Returns a doc guaranteed to satisfy the current
 * `CallSheetDoc` shape.
 *
 * `sheetId` seeds the deterministic IDs given to shots saved before shot IDs
 * existed, so pass the stored sheet's ID on every read and save. Pass
 * `previousShots` when migrating an AI reply about an existing document.
 */
export function migrateDoc(
  raw: unknown,
  sheetId = '',
  previousShots: MandatoryShotRow[] = [],
): CallSheetDoc {
  const v = (isRecord(raw) ? raw : {}) as Partial<CallSheetDoc> & Record<string, unknown>;
  const schemaVersion = typeof v.schemaVersion === 'number' ? v.schemaVersion : 1;

  // Carry over V1 locations[] as the V2 referenceLocations[] panel. Records
  // are otherwise identical — the rename signals that this isn't an edited
  // section anymore but a derived reference.
  const v1Locations = extractLocations(v.locations);
  const referenceLocations: ReferenceLocationRow[] = Array.isArray(v.referenceLocations)
    ? extractReferenceLocations(v.referenceLocations)
    : v1Locations.map((loc) => ({
        id: loc.id,
        label: loc.label,
        address: loc.address,
        parking: loc.parking,
        parkingAccess: loc.parkingAccess,
        contactName: loc.contactName,
        contactMobile: loc.contactMobile,
        inductionUrl: loc.inductionUrl,
        nearestHospital: loc.nearestHospital,
        notes: loc.notes,
      }));

  // V1 schedule rows had no location field. If the shoot used exactly one
  // location, attach that label to every schedule row — accurate for the
  // common single-venue case. With multiple V1 locations we can't infer
  // mapping, so rows stay un-located and the user fills them in.
  const onlyLocationLabel =
    schemaVersion < 2 && referenceLocations.length === 1 ? referenceLocations[0].label : undefined;
  const schedule = extractSchedule(v.schedule, onlyLocationLabel);

  // docType added in Phase 13. Pre-existing docs are all full call sheets.
  const docType: CallSheetDoc['docType'] =
    v.docType === 'client_schedule' ? 'client_schedule' : 'callsheet';
  const captures: NonNullable<CallSheetDoc['captures']> =
    v.captures === 'photo' || v.captures === 'both' ? v.captures : 'video';

  return {
    schemaVersion: CALLSHEET_SCHEMA_VERSION,
    docType,
    // Phase 3 Stage A — structural/public section choices must survive every
    // save. Store only known full-call-sheet extras; keep the user's order,
    // dedupe, and collapse malformed/empty input to undefined.
    enabledSections: extractEnabledSections(v.enabledSections),
    client: extractClient(v.client),
    project: extractProject(v.project),
    producer: extractProducer(v.producer),
    referenceLocations,
    crew: extractCrew(v.crew),
    talent: extractTalent(v.talent),
    attendees: extractAttendees(v.attendees),
    interviewQuestions: extractInterviewQuestions(v.interviewQuestions),
    schedule,
    filmingSpecs: extractFilmingSpecs(v.filmingSpecs),
    mandatoryShots: assignShotIds(extractMandatoryShots(v.mandatoryShots, captures), sheetId, previousShots),
    captures,
    mandatoryShotsGroupBy:
      v.mandatoryShotsGroupBy === 'location' ? 'location' : 'time',
    safety: extractSafety(v.safety),
    complianceRules: Array.isArray(v.complianceRules)
      ? (v.complianceRules as string[]).filter((s) => typeof s === 'string')
      : [],
    clarifications: extractClarifications(v.clarifications),
    dataWrangle: typeof v.dataWrangle === 'string' ? v.dataWrangle : '',
    dressCode: typeof v.dressCode === 'string' ? v.dressCode : '',
    catering: typeof v.catering === 'string' ? v.catering : '',
    wardrobe: typeof v.wardrobe === 'string' ? v.wardrobe : '',
    props: typeof v.props === 'string' ? v.props : '',
    audioNotes: typeof v.audioNotes === 'string' ? v.audioNotes : '',
    extraNotes: typeof v.extraNotes === 'string' ? v.extraNotes : '',
    internalNotes: typeof v.internalNotes === 'string' ? v.internalNotes : '',
    branding: extractBranding(v.branding),
    wrapNotes: extractWrapNotes(v.wrapNotes),
    wrapClientNote: typeof v.wrapClientNote === 'string' ? v.wrapClientNote : undefined,
    nextSteps: Array.isArray(v.nextSteps)
      ? v.nextSteps.filter((step): step is string => typeof step === 'string') : undefined,
    showDayReport: typeof v.showDayReport === 'boolean' ? v.showDayReport : undefined,
    scriptMeta: extractScriptMeta(v.scriptMeta),
    scriptSyncHealth: extractScriptSyncHealth(v.scriptSyncHealth),
    scriptLinks: extractScriptLinks(v.scriptLinks),
    scriptLinksDismissed: extractStringIds(v.scriptLinksDismissed),
    scriptLinksScope: v.scriptLinksScope === 'job' ? 'job' : 'client',
    // R6.5 — pass through doc-level sync timestamp. Plain integer; defensive
    // typeof check so a corrupt value doesn't poison the doc. Pre-R6.5 docs
    // simply have no value here, which is the same as "never synced" → the
    // on-editor-open silent-sync path will pick them up immediately.
    interviewQuestionsSyncedAt:
      typeof v.interviewQuestionsSyncedAt === 'number' ? v.interviewQuestionsSyncedAt : undefined,
    // R7 / WP1: must be listed explicitly or it strips on the save
    // round-trip (the same class of bug that R1.2a fixed for `branding`).
    // Explicit 'on' / 'off' wins; the legacy `roleViewsDisabled: true` reads
    // as 'off'; anything else is undefined (the per-doc default applies).
    roleViews: extractRoleViews(v.roleViews, v.roleViewsDisabled),
    onCameraRelease: v.onCameraRelease === true ? true : undefined,
    lensSections: extractLensSections(v.lensSections),
    agentTools: v.agentTools === 'on' || v.agentTools === 'off' ? v.agentTools : undefined,
    // WP1: origin marker for the role-gate default (see roleViewsEnabled).
    createdAs: v.createdAs === 'callsheet' ? 'callsheet' : undefined,
    // Client-experience wave (PLAN_V3 Phase 2) — every field explicit here
    // or it silently strips on the next save (Hard rule 2). Defensive
    // extraction mirrors the branding/wrapNotes pattern.
    personalNote: extractPersonalNote(v.personalNote),
    producerCard: extractEnabledFlag(v.producerCard),
    weatherCard: extractEnabledFlag(v.weatherCard),
    faq: extractFaq(v.faq),
    prepGuide: extractEnabledFlag(v.prepGuide),
    exteriorShoot: v.exteriorShoot === true ? true : undefined,
    calendarUidScheme: v.calendarUidScheme === 'v2' ? 'v2' : undefined,
  };
}

function extractClient(raw: unknown): CallSheetDoc['client'] {
  const row = isRecord(raw) ? raw : {};
  return {
    name: stringValue(row.name),
    ...(optionalString(row.id) !== undefined ? { id: optionalString(row.id) } : {}),
    ...(optionalString(row.contactName) !== undefined ? { contactName: optionalString(row.contactName) } : {}),
    ...(optionalString(row.contactRole) !== undefined ? { contactRole: optionalString(row.contactRole) } : {}),
    ...(optionalString(row.contactEmail) !== undefined ? { contactEmail: optionalString(row.contactEmail) } : {}),
    ...(optionalString(row.contactMobile) !== undefined ? { contactMobile: optionalString(row.contactMobile) } : {}),
  };
}

function extractProject(raw: unknown): CallSheetDoc['project'] {
  const row = isRecord(raw) ? raw : {};
  return {
    title: stringValue(row.title),
    overview: stringValue(row.overview),
    shootDates: Array.isArray(row.shootDates)
      ? row.shootDates.map((date) => optionalString(date)).filter((date): date is string => date !== undefined)
      : [],
    ...(optionalString(row.weather) !== undefined ? { weather: optionalString(row.weather) } : {}),
  };
}

function extractProducer(raw: unknown): CallSheetDoc['producer'] {
  const row = isRecord(raw) ? raw : {};
  return {
    name: stringValue(row.name),
    mobile: stringValue(row.mobile),
    email: stringValue(row.email),
    ...(typeof row.remote === 'boolean' ? { remote: row.remote } : {}),
    ...(typeof row.onSetContact === 'string' ? { onSetContact: row.onSetContact } : {}),
    ...(optionalString(row.start) !== undefined ? { start: optionalString(row.start) } : {}),
    ...(optionalString(row.end) !== undefined ? { end: optionalString(row.end) } : {}),
  };
}

function extractLocations(raw: unknown): LocationRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    label: stringValue(row.label),
    address: stringValue(row.address),
    ...(optionalString(row.id) !== undefined ? { id: optionalString(row.id) } : {}),
    ...(optionalString(row.parking) !== undefined ? { parking: optionalString(row.parking) } : {}),
    ...(optionalString(row.parkingAccess) !== undefined ? { parkingAccess: optionalString(row.parkingAccess) } : {}),
    ...(optionalString(row.contactName) !== undefined ? { contactName: optionalString(row.contactName) } : {}),
    ...(optionalString(row.contactMobile) !== undefined ? { contactMobile: optionalString(row.contactMobile) } : {}),
    ...(normalizeStoredUrl(optionalString(row.inductionUrl)) ? { inductionUrl: normalizeStoredUrl(optionalString(row.inductionUrl)) } : {}),
    ...(optionalString(row.nearestHospital) !== undefined ? { nearestHospital: optionalString(row.nearestHospital) } : {}),
    ...(optionalString(row.notes) !== undefined ? { notes: optionalString(row.notes) } : {}),
  }));
}

function extractReferenceLocations(raw: unknown): ReferenceLocationRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    label: stringValue(row.label),
    ...(typeof row.roomBooked === 'boolean' ? { roomBooked: row.roomBooked } : {}),
    ...(typeof row.buildingAccessTime === 'string' ? { buildingAccessTime: row.buildingAccessTime } : {}),
    ...(optionalString(row.id) !== undefined ? { id: optionalString(row.id) } : {}),
    ...(optionalString(row.address) !== undefined ? { address: optionalString(row.address) } : {}),
    ...(optionalString(row.parking) !== undefined ? { parking: optionalString(row.parking) } : {}),
    ...(optionalString(row.parkingAccess) !== undefined ? { parkingAccess: optionalString(row.parkingAccess) } : {}),
    ...(optionalString(row.contactName) !== undefined ? { contactName: optionalString(row.contactName) } : {}),
    ...(optionalString(row.contactMobile) !== undefined ? { contactMobile: optionalString(row.contactMobile) } : {}),
    ...(normalizeStoredUrl(optionalString(row.inductionUrl)) ? { inductionUrl: normalizeStoredUrl(optionalString(row.inductionUrl)) } : {}),
    ...(optionalString(row.nearestHospital) !== undefined ? { nearestHospital: optionalString(row.nearestHospital) } : {}),
    ...(optionalString(row.notes) !== undefined ? { notes: optionalString(row.notes) } : {}),
  }));
}

function extractCrew(raw: unknown): CallSheetDoc['crew'] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    name: stringValue(row.name),
    role: stringValue(row.role),
    ...(row.discipline === 'video' || row.discipline === 'photo' || row.discipline === 'both'
      ? { discipline: row.discipline } : {}),
    ...(optionalString(row.id) !== undefined ? { id: optionalString(row.id) } : {}),
    ...(optionalString(row.mobile) !== undefined ? { mobile: optionalString(row.mobile) } : {}),
    ...(optionalString(row.email) !== undefined ? { email: optionalString(row.email) } : {}),
    ...(optionalString(row.start) !== undefined ? { start: optionalString(row.start) } : {}),
    ...(optionalString(row.end) !== undefined ? { end: optionalString(row.end) } : {}),
    ...(optionalString(row.equipment) !== undefined ? { equipment: optionalString(row.equipment) } : {}),
  }));
}

function extractTalent(raw: unknown): CallSheetDoc['talent'] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    name: stringValue(row.name),
    ...(optionalString(row.role) !== undefined ? { role: optionalString(row.role) } : {}),
    ...(optionalString(row.activity) !== undefined ? { activity: optionalString(row.activity) } : {}),
    ...(optionalString(row.callTime) !== undefined ? { callTime: optionalString(row.callTime) } : {}),
    ...(optionalString(row.wrapTime) !== undefined ? { wrapTime: optionalString(row.wrapTime) } : {}),
    ...(optionalString(row.dressCode) !== undefined ? { dressCode: optionalString(row.dressCode) } : {}),
    ...(optionalString(row.notes) !== undefined ? { notes: optionalString(row.notes) } : {}),
  }));
}

function extractAttendees(raw: unknown): CallSheetDoc['attendees'] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    name: stringValue(row.name),
    ...(optionalString(row.role) !== undefined ? { role: optionalString(row.role) } : {}),
    ...(optionalString(row.organisation) !== undefined ? { organisation: optionalString(row.organisation) } : {}),
    ...(optionalString(row.mobile) !== undefined ? { mobile: optionalString(row.mobile) } : {}),
    ...(optionalString(row.email) !== undefined ? { email: optionalString(row.email) } : {}),
    ...(optionalString(row.notes) !== undefined ? { notes: optionalString(row.notes) } : {}),
  }));
}

function extractInterviewQuestions(raw: unknown): CallSheetDoc['interviewQuestions'] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => {
    const rawQuestions = Array.isArray(row.questions) ? row.questions : [];
    const indices = rawQuestions.flatMap((q, i) => optionalString(q) === undefined ? [] : [i]);
    const hints = Array.isArray(row.hints) ? indices.map((i) => {
      const entry = (row.hints as unknown[])[i];
      return Array.isArray(entry) && entry.every((hint) => typeof hint === 'string') ? entry : [];
    }) : undefined;
    return {
      audience: stringValue(row.audience),
      questions: indices.map((i) => optionalString(rawQuestions[i])!),
      ...(hints?.some((entry) => entry.length > 0) ? { hints } : {}),
      ...(typeof row.internal === 'boolean' ? { internal: row.internal } : {}),
      ...(typeof row.shared === 'boolean' ? { shared: row.shared } : {}),
      ...(optionalString(row.sourceScriptId) !== undefined ? { sourceScriptId: optionalString(row.sourceScriptId) } : {}),
    };
  });
}

function extractSchedule(raw: unknown, defaultLocation?: string): ScheduleRow[] {
  if (!Array.isArray(raw)) return [];
  const highlights = new Set<ScheduleHighlight>(['key', 'meal', 'move']);
  return raw.filter(isRecord).map((row) => ({
    time: stringValue(row.time),
    activity: stringValue(row.activity),
    ...(typeof row.fixed === 'boolean' ? { fixed: row.fixed } : {}),
    ...(optionalString(row.location) ?? defaultLocation
      ? { location: optionalString(row.location) ?? defaultLocation }
      : {}),
    ...(optionalString(row.notes) !== undefined ? { notes: optionalString(row.notes) } : {}),
    ...(typeof row.day === 'number' && Number.isFinite(row.day) ? { day: row.day } : {}),
    ...(highlights.has(row.highlight as ScheduleHighlight)
      ? { highlight: row.highlight as ScheduleHighlight }
      : {}),
  }));
}

function extractFilmingSpecs(raw: unknown): CallSheetDoc['filmingSpecs'] {
  const row = isRecord(raw) ? raw : {};
  const fields = ['colourProfile', 'frameRates', 'resolution', 'codec', 'extras'] as const;
  return Object.fromEntries(
    fields.flatMap((field) => {
      const value = optionalString(row[field]);
      return value === undefined ? [] : [[field, value]];
    }),
  );
}

function extractSafety(raw: unknown): CallSheetDoc['safety'] {
  const row = isRecord(raw) ? raw : {};
  return {
    emergency: stringValue(row.emergency, 'In an emergency, dial 000.'),
    hospitalAddress: stringValue(row.hospitalAddress, 'TBC — confirm before shoot day'),
    ...(optionalString(row.protocols) !== undefined ? { protocols: optionalString(row.protocols) } : {}),
  };
}

function extractClarifications(raw: unknown): CallSheetDoc['clarifications'] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).flatMap((row) => {
    const id = optionalString(row.id);
    const question = optionalString(row.question);
    const status = row.status;
    if (!id || !question || (status !== 'pending' && status !== 'answered' && status !== 'dismissed')) return [];
    return [{
      id,
      question,
      status,
      ...(optionalString(row.fieldHint) !== undefined ? { fieldHint: optionalString(row.fieldHint) } : {}),
      ...(optionalString(row.suggestedAnswer) !== undefined ? { suggestedAnswer: optionalString(row.suggestedAnswer) } : {}),
      ...(optionalString(row.userAnswer) !== undefined ? { userAnswer: optionalString(row.userAnswer) } : {}),
    }];
  });
}

const SHOT_CATEGORIES = new Set<ShotCategory>([
  'hero',
  'broll',
  'interview',
  'cutaway',
  'group',
  'social',
]);

function coerceShotString(raw: unknown): string | undefined {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return undefined;
}

function extractMandatoryShots(
  raw: unknown,
  captures: NonNullable<CallSheetDoc['captures']>,
): MandatoryShotRow[] {
  if (!Array.isArray(raw)) return [];
  const shots: MandatoryShotRow[] = [];

  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const row = item as Record<string, unknown>;
    const description = coerceShotString(row.description);
    if (description === undefined) continue;

    const time = coerceShotString(row.time);
    const location = coerceShotString(row.location);
    const category = SHOT_CATEGORIES.has(row.category as ShotCategory)
      ? (row.category as ShotCategory)
      : undefined;
    const explicitMedia = row.media === 'video' || row.media === 'photo' ? row.media : undefined;
    const media = explicitMedia ?? (captures === 'photo' ? 'photo' : undefined);
    const starred = typeof row.starred === 'boolean' ? row.starred : undefined;

    shots.push({
      ...(typeof row.id === 'string' ? { id: row.id } : {}),
      ...(time !== undefined ? { time } : {}),
      description,
      ...(location !== undefined ? { location } : {}),
      ...(category !== undefined ? { category } : {}),
      ...(starred !== undefined ? { starred } : {}),
      ...(media !== undefined ? { media } : {}),
    });
  }

  return shots;
}

function extractRoleViews(raw: unknown, legacyDisabled: unknown): CallSheetDoc['roleViews'] {
  if (raw === 'on' || raw === 'off') return raw;
  return legacyDisabled === true ? 'off' : undefined;
}

function extractLensSections(raw: unknown): CallSheetDoc['lensSections'] {
  if (!isRecord(raw)) return undefined;
  const prototype = Object.getPrototypeOf(raw);
  if (prototype !== Object.prototype && prototype !== null) return undefined;

  const known = new Set<Section>(SECTIONS.map((section) => section.id).filter((id) => id !== 'internal'));
  const result: NonNullable<CallSheetDoc['lensSections']> = {};
  for (const lens of ['crew', 'talent', 'client'] as const) {
    if (!Object.hasOwn(raw, lens)) continue;
    const picks = raw[lens];
    if (!Array.isArray(picks)) continue;
    const valid = [...new Set(picks.filter((id): id is Section =>
      typeof id === 'string' && known.has(id as Section),
    ))];
    if (picks.length > 0 && valid.length === 0) continue;
    result[lens] = valid;
  }
  return Object.keys(result).length ? result : undefined;
}

function extractEnabledSections(raw: unknown): CallSheetDoc['enabledSections'] {
  if (!Array.isArray(raw)) return undefined;

  const seen = new Set<Section>();
  const enabled: Section[] = [];
  for (const item of raw) {
    if (!ENABLED_SECTION_SET.has(item as Section) || seen.has(item as Section)) continue;
    const section = item as Section;
    seen.add(section);
    enabled.push(section);
  }
  // An array ALWAYS stays an array — [] means "base sections only", which is
  // strictly narrower than undefined's "all sections". Collapsing an empty or
  // junk-only list to undefined would silently EXPAND what a doc exposes
  // (crew, talent, safety…), so junk ids drop but the array shape survives.
  return enabled;
}

/** Personal note: object with an `enabled` boolean + `text` string. Absent
 *  or malformed → undefined (nothing renders, nothing bloats the JSON). */
function extractPersonalNote(raw: unknown): CallSheetDoc['personalNote'] {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  return {
    enabled: r.enabled === true,
    text: typeof r.text === 'string' ? r.text : '',
  };
}

/** producerCard / weatherCard / prepGuide share the shape `{ enabled: boolean }`. The
 *  distinction between undefined (= default by docType / talent presence)
 *  and an explicit {enabled:false} matters, so a present-but-malformed
 *  object coerces to {enabled:false} rather than undefined. */
function extractEnabledFlag(raw: unknown): { enabled: boolean } | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  return { enabled: (raw as Record<string, unknown>).enabled === true };
}

/** FAQ rows: keep only well-formed {id, question, answer} string triples
 *  (blank question+answer rows are dropped; a row still being typed keeps
 *  its place as long as either field has content). undefined when empty. */
function extractFaq(raw: unknown): CallSheetDoc['faq'] {
  if (!Array.isArray(raw)) return undefined;
  const out: NonNullable<CallSheetDoc['faq']> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id.trim() : '';
    const question = typeof r.question === 'string' ? r.question : '';
    const answer = typeof r.answer === 'string' ? r.answer : '';
    if (!id || (!question.trim() && !answer.trim())) continue;
    out.push({ id, question, answer });
  }
  return out.length > 0 ? out : undefined;
}

// R6.2 — preserve `scriptLinks` snapshots through every save. Mirrors the
// branding/wrapNotes pattern: undefined when empty, only well-formed entries
// kept, so a malformed payload can't poison the doc. R6.3 stopped writing
// to this field from the editor, but the worker still reads it as a
// manifest-unreachable fallback so we keep the round-trip preservation.
function extractScriptLinks(raw: unknown): CallSheetDoc['scriptLinks'] {
  if (!Array.isArray(raw)) return undefined;
  const out: NonNullable<CallSheetDoc['scriptLinks']> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const id = typeof r.scriptgen_script_id === 'string' ? r.scriptgen_script_id.trim() : '';
    const title = typeof r.title === 'string' ? r.title.trim() : '';
    const url = normalizeStoredUrl(typeof r.url === 'string' ? r.url : undefined);
    if (!id || !title || !url) continue;
    const meta = parsePublicScriptMeta(r.meta);
    out.push({ scriptgen_script_id: id, title, url, ...(meta ? { meta } : {}) });
  }
  return out.length > 0 ? out : undefined;
}

// String identifiers: dedupe and drop empties. Undefined when empty.
function extractStringIds(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (trimmed) seen.add(trimmed);
  }
  return seen.size > 0 ? [...seen] : undefined;
}

function extractBranding(raw: unknown): CallSheetDoc['branding'] {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: NonNullable<CallSheetDoc['branding']> = {};
  if (typeof r.whiteLabel === 'boolean') out.whiteLabel = r.whiteLabel;
  if (typeof r.wordmark === 'string' && r.wordmark.trim()) out.wordmark = r.wordmark.trim();
  if (typeof r.accentColor === 'string' && r.accentColor.trim())
    out.accentColor = r.accentColor.trim();
  return Object.keys(out).length > 0 ? out : undefined;
}

function extractWrapNotes(raw: unknown): CallSheetDoc['wrapNotes'] {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: NonNullable<CallSheetDoc['wrapNotes']> = {};
  if (typeof r.production === 'string' && r.production.trim()) out.production = r.production;
  if (typeof r.footage === 'string' && r.footage.trim()) out.footage = r.footage;
  if (typeof r.issues === 'string' && r.issues.trim()) out.issues = r.issues;
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Parses doc_json and runs the migrator. Returns a doc that always
 * satisfies the current schema, even if the JSON is corrupt — falls back
 * to a near-empty doc rather than throwing.
 */
export function parseAndMigrate(docJson: string, sheetId = ''): CallSheetDoc {
  let raw: unknown;
  try {
    raw = JSON.parse(docJson);
  } catch {
    raw = {};
  }
  return migrateDoc(raw, sheetId);
}

/**
 * Post-process an AI-drafted CallSheetDoc to fix the common rule violations
 * that recur even with explicit prompt instructions:
 *
 *   1. Venue prefix in schedule[].location and referenceLocations[].label
 *      — the AI tends to write "Werribee Law Courts — Level 2" everywhere
 *      when the schedule row should just say "Level 2". Detect the shared
 *      prefix and strip.
 *   2. Dress code boilerplate ("own choice — natural and comfortable",
 *      "talent's own attire", etc.) — blank these. Better empty than noise.
 *
 * Safe to run multiple times. Only acts when the pattern is unambiguous.
 */
export function normalizeAiDraft(doc: CallSheetDoc): CallSheetDoc {
  const next: CallSheetDoc = { ...doc };

  const prefix = detectVenuePrefix(next.referenceLocations);
  if (prefix) {
    next.schedule = next.schedule.map((row) => ({
      ...row,
      location: stripPrefix(row.location ?? '', prefix) || row.location,
    }));
    next.mandatoryShots = next.mandatoryShots.map((shot) => ({
      ...shot,
      location: shot.location ? stripPrefix(shot.location, prefix) || shot.location : shot.location,
    }));
    if (next.referenceLocations.length > 1) {
      next.referenceLocations = next.referenceLocations.map((l) => ({
        ...l,
        label: stripPrefix(l.label, prefix) || l.label,
      }));
    }
  }

  const dressBoilerplate =
    /^(own\s*choice|talent'?s?\s*own|comfortable\s+(casual|attire)|standard\s*attire|casual\s+and\s+comfortable|natural\s+and\s+comfortable)/i;
  next.talent = next.talent.map((t) => {
    let dressCode = t.dressCode;
    if (dressCode && dressBoilerplate.test(dressCode.trim())) {
      dressCode = '';
    }
    // Truncate talent.notes to first sentence when AI dumps a paragraph.
    // Notes >25 words get clipped to the first sentence — the rest is fluff.
    let notes = t.notes;
    if (notes && notes.split(/\s+/).filter(Boolean).length > 25) {
      const match = notes.match(/^[^.!?]*[.!?]/);
      if (match) notes = match[0].trim();
    }
    return { ...t, dressCode, notes };
  });

  // Auto-set schedule highlights based on activity text. The AI keeps
  // ignoring the prompt instruction to set highlight on key/meal/move rows,
  // so we detect them server-side and fill in. Preserves any explicitly set
  // highlight; only fills nulls/undefined.
  next.schedule = next.schedule.map((row) => {
    if (row.highlight) return row;
    const activity = (row.activity ?? '').trim();
    const detected = detectHighlight(activity);
    if (detected) return { ...row, highlight: detected };
    return row;
  });

  return next;
}

/**
 * Detect a schedule row's highlight from its activity text. Used by both
 * the server-side normalizer (post-AI-draft) and the display layer (fallback
 * for older docs with null highlights). Pure / idempotent.
 */
export function detectHighlight(activity: string): 'key' | 'meal' | 'move' | undefined {
  const a = activity.toLowerCase();
  // Order matters — MOVE first (some MOVE activities mention "to lunch" etc.)
  if (/^move(\s+to\b|$)/.test(a) || /^→/.test(activity)) return 'move';
  if (/^wrap\b|^pack[\s-]?down\b|^pack\s+up\b/.test(a)) return 'key';
  if (/^crew\s+call\b|^bump[\s-]?in\b|^crew\s+brief\b|^talent\s+arriv/.test(a)) return 'key';
  if (/\b(lunch|bbq|barbecue|barbeque|catered|catering|breakfast|dinner|tea\s+break|morning\s+tea|afternoon\s+tea)\b/.test(a)) return 'meal';
  return undefined;
}

/**
 * Find the longest common starting substring across all reference location
 * labels — typically the venue name when the AI created one entry per
 * sub-room. Returns null if no meaningful shared prefix exists.
 */
export function detectVenuePrefix(refs: CallSheetDoc['referenceLocations']): string | null {
  if (!refs || refs.length < 2) return null;
  const labels = refs.map((r) => r.label).filter((l) => l && l.length > 0);
  if (labels.length < 2) return null;

  let prefix = labels[0];
  for (let i = 1; i < labels.length; i++) {
    while (prefix.length > 0 && !labels[i].startsWith(prefix)) {
      prefix = prefix.slice(0, -1);
    }
    if (!prefix) return null;
  }

  prefix = prefix.replace(/\s*[—\-:|·/]\s*$/, '').trim();
  return prefix.length >= 6 ? prefix : null;
}

export function stripPrefix(value: string, prefix: string): string {
  if (!value.startsWith(prefix)) return value;
  return value.slice(prefix.length).replace(/^\s*[—\-:|·/]\s*/, '').trim();
}

function extractScriptMeta(raw: unknown): CallSheetDoc['scriptMeta'] {
  if (!isRecord(raw)) return undefined;
  const entries = Object.entries(raw).flatMap(([id, value]) => {
    const meta = parseScriptMeta(value);
    return id.trim() && meta ? [[id, meta] as const] : [];
  });
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function extractScriptSyncHealth(raw: unknown): CallSheetDoc['scriptSyncHealth'] {
  if (!isRecord(raw) || typeof raw.checkedAt !== 'number'
    || !Number.isFinite(raw.checkedAt) || raw.checkedAt < 0
    || (raw.state !== 'ok' && raw.state !== 'degraded' && raw.state !== 'unavailable')) return undefined;
  const failedScriptIds = extractStringIds(raw.failedScriptIds);
  return { checkedAt: raw.checkedAt, state: raw.state, ...(failedScriptIds ? { failedScriptIds } : {}) };
}
